import React, { useEffect } from "react";
import { Auth } from "aws-amplify";
// ANT DESIGN COMPONENT
import { Drawer, Avatar, Button, Tag, Tooltip, Spin } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faRightFromBracket,
  faStar,
  faWifi,
} from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import {
  updateOpenDrawerUser,
  updateGlobalOrganizationName,
  updateGlobalRolName,
  updateGlobalOrganizationId,
  updateGlobalRolId,
} from "../../redux/home/homeSlice";
import { useLazyGetRoleQuery } from "../../redux/roles/roleAPI";
import { updatePermissions } from "../../redux/users/userSlice";
// COMPONENTS
import ActionFiringPreference from "./ActionFiringPreference";
import Appearance from "./Appearance";
import Internationalization from "./Internationalization";
import MultiFactorAuth from "./MultiFactorAuth";
import DrawerOrganizationInformation from "../components/drawer/DrawerOrganizationInformation";
import DrawerRoleInformation from "../components/drawer/DrawerRoleInformation";
import DrawerUserInformation from "../components/drawer/DrawerUserInformation";
import GlobalConfiguration from "./GlobalConfiguration";
import LiftnetInformation from "./LiftnetInformation";
import ButtonClose from "../components/buttons/ButtonClose";
import TagSelectOrganizationsLight from "../components/select/TagSelectOrganizationsLight";
import TagSelectOrganizationRoleLight from "../components/select/TagSelectOrganizationRoleLight";
import GlobalAlert2 from "./GlobalAlert2";
import AppNotifications from "./AppNotifications";
// STYLES
import usaFlag from "../../assets/i18n/unitedStatesFlag.png";
import spainFlag from "../../assets/i18n/spainFlag.png";
// INTERFACES
import { RootState } from "../../app/store";
import { ZERO_UUID_STRING } from "../../utils/uuid";
// AUTHORIZATION
import GETJwtToken, { clearOfflineSession } from "../../redux/authentication/authentication";
import { usePostLogoutMutation } from "../../redux/users/userAPI";
// ORG-SCOPE
import isMasterAdmin from "../../utils/isMasterAdmin";
import useGetAllOrganizationsLight from "../../customHooks/GET_requests/useGetAllOrganizationsLight";

const YourProfileDrawer = () => {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const {
    userLogged,
    language,
    openDrawerUser,
    globalOrganizationName,
    globalRolName,
    globalOrganizationId,
    globalRolId,
  } = useSelector((state: RootState) => state.home);
  const { socketConnection, showHeaderConfiguration, favoriteGridName } = useSelector(
    (state: RootState) => state.monitoring,
  );
  const hasPreferredGrid = !!userLogged?.prefGridId && userLogged.prefGridId !== ZERO_UUID_STRING;

  // Cross-org focus: scope to orgs the caller can actually VIEW a bank in
  // (/orgs/light?with_banks=true — org tree ∩ per-bank scope), so the switcher
  // matches the monitoring cascader. A master admin, or any parent whose visible
  // set has more than one org, may focus the header selector on a sub-org
  // (banks/reports/admin lists key off globalOrganizationId). A user who can see
  // banks in only one org gets the read-only tags. Role impersonation + "reset to
  // master" remain master-admin only.
  const { organizationsLight } = useGetAllOrganizationsLight(true);
  const isMaster = isMasterAdmin(userLogged, globalOrganizationId);
  const canFocusSubOrg = isMaster || (organizationsLight?.length ?? 0) > 1;

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [
    triggerGetRole,
    {
      isLoading: isLoadingGetRole,
      isFetching: isFetchingGetRole,
      isError: isErrorGetRole,
      error: errorGetRole,
    },
  ] = useLazyGetRoleQuery();
  const [triggerLogout] = usePostLogoutMutation();

  // ************************************************* */
  // FUNCTIONS *************************************** */
  const showDrawer = () => {
    dispatch(updateOpenDrawerUser(true));
  };

  const onClickClose = () => {
    dispatch(updateOpenDrawerUser(false));
  };

  const signOut = async () => {
    // Revoke server-side first: clearing the browser alone left the refresh
    // token valid for its full TTL (LIFT-51). Never let this block the local
    // sign-out — if the call fails the user must still get logged out here.
    try {
      const token = await GETJwtToken();
      await triggerLogout({ token }).unwrap();
    } catch {
      // Already logged out, offline, or the token expired: nothing to revoke.
    }

    // global: true asks Cognito to revoke the refresh token on its side too,
    // which our API cannot do for it.
    await Auth.signOut({ global: true }).catch(() => Auth.signOut());
    localStorage.setItem("useLoggedAmplify", "false");
    // The offline session's access and refresh tokens (LIFT-1085).
    clearOfflineSession();
    window.location.href = `${process.env.REACT_APP_LOGIN_ROUTE}`;
  };

  // Non-master org switcher only (canFocusSubOrg without isMaster): these
  // operators never get a role picker, so their globalRolId already stays
  // pinned to their own role and an org change never needs to touch it.
  const onChangeIdOrganization = (idOrganization: string, record: any) => {
    dispatch(updateGlobalOrganizationId(record.value));
    dispatch(updateGlobalOrganizationName(record.label));
  };

  const onChangeIdRole = async (newValue: string) => {
    try {
      const token = await GETJwtToken();
      const result = await triggerGetRole({ token, roleId: newValue });
      if (result && result.data && result.data.data) {
        const permissionsRaw = result.data.data.menuJson ?? result.data.data.menu;
        const permissionsParsed =
          typeof permissionsRaw === "string"
            ? JSON.parse(permissionsRaw || "{}")
            : permissionsRaw || {};
        dispatch(updatePermissions(permissionsParsed));
        dispatch(updateGlobalRolId(result.data.data.id));
        dispatch(updateGlobalRolName(result.data.data.name));
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  // Master-admin header switcher (TagSelectOrganizationRoleLight): its Save
  // commits an organization AND a role together in one action, so there is
  // never a moment where `permissions` reflects a role that doesn't belong
  // to the org now being viewed — the reset-to-own-role fallback the
  // org-only picker above needed doesn't apply here.
  const onSelectOrganizationAndRole = (
    orgValue: string,
    orgRecord: any,
    roleValueToApply: string,
  ) => {
    dispatch(updateGlobalOrganizationId(orgRecord?.value ?? orgValue));
    dispatch(updateGlobalOrganizationName(orgRecord?.label ?? ""));
    onChangeIdRole(roleValueToApply);
  };

  const onClickResetToMasterAdmin = () => {
    if (userLogged.roleMenu) {
      dispatch(updatePermissions(userLogged.roleMenu));
    }
    dispatch(updateGlobalOrganizationId(userLogged.organizationId));
    dispatch(updateGlobalOrganizationName(String(userLogged.org.name ?? "")));
    onChangeIdRole(userLogged.roleId);
  };

  // *********************************************** */
  // USE EFFECT ************************************ */
  useEffect(() => {
    if (userLogged) {
      if (userLogged.org && userLogged.org.id) {
        dispatch(updateGlobalOrganizationId(String(userLogged.org.id ?? "")));
        dispatch(updateGlobalOrganizationName(String(userLogged.org.name ?? "")));
      }
      if (userLogged.role && userLogged.role.id) {
        dispatch(updateGlobalRolId(userLogged.role.id));
        dispatch(updateGlobalRolName(userLogged.role.name || ""));
      }
    }
  }, [userLogged]);

  // ************************************************* */
  // COMPONENT ************************************** */
  return (
    <div>
      <Spin spinning={isLoadingGetRole || isFetchingGetRole}>
        <div className="generalStyles__flex">
          {/** ************************************************* */}
          {/** HEADER AND TAGS */}
          <div className="generalStyles__flex mt-2">
            {canFocusSubOrg && showHeaderConfiguration ? (
              <div className="generalStyles__flex">
                {isMaster ? (
                  <>
                    {/* Single Button ("<org> / <role>"): opens the org+role
                        2-step wizard — step 1 picks an org (not applied),
                        its footer's primary button becomes "Select role"
                        and moves to step 2, scoped to that org; Save there
                        commits both together. */}
                    <div className="generalStyles__mrFix" style={{ marginTop: "-6px" }}>
                      <TagSelectOrganizationRoleLight
                        value={globalOrganizationId}
                        roleValue={globalRolId}
                        roleLabel={globalRolName}
                        onSave={onSelectOrganizationAndRole}
                        disabledInput={false}
                        withBanks
                      />
                    </div>
                    <div className="generalStyles__mrFix" style={{ marginTop: "-6px" }}>
                      <Button
                        size="small"
                        className="buttonStyle__1 generalStyles__width100px"
                        onClick={onClickResetToMasterAdmin}
                      >
                        {t("general.reset")}
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="generalStyles__mrFix">
                      <TagSelectOrganizationsLight
                        value={globalOrganizationId}
                        onChangeIdOrganization={onChangeIdOrganization}
                        disabledInput={false}
                        withBanks
                      />
                    </div>
                    <div>
                      <Tooltip title={globalRolName}>
                        <Tag
                          color="purple"
                          className="generalStyles__width150px generalStyles__textCenter generalStyles__textEllipsis"
                        >
                          {globalRolName || "--"}
                        </Tag>
                      </Tooltip>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <>
                <div>
                  <Tooltip title={globalOrganizationName}>
                    <Tag
                      color="blue"
                      className="generalStyles__width150px generalStyles__textCenter generalStyles__textEllipsis"
                    >
                      {globalOrganizationName || "--"}
                    </Tag>
                  </Tooltip>
                </div>
                <div>
                  <Tooltip title={globalRolName}>
                    <Tag
                      color="purple"
                      className="generalStyles__width150px generalStyles__textCenter generalStyles__textEllipsis"
                    >
                      {globalRolName || "--"}
                    </Tag>
                  </Tooltip>
                </div>
              </>
            )}
            {hasPreferredGrid && (
              <div>
                <Tag
                  color="gold"
                  className="generalStyles__width150px generalStyles__textCenter generalStyles__textEllipsis"
                >
                  <FontAwesomeIcon
                    className="generalStyles__userLoggedIcon generalStyles__mrFix"
                    icon={faStar}
                  />
                  {favoriteGridName || userLogged.prefGridId}
                </Tag>
              </div>
            )}
            {socketConnection === "EDGE" && (
              <div>
                <Tag
                  color="green"
                  className="generalStyles__width150px generalStyles__textCenter generalStyles__textEllipsis"
                >
                  <FontAwesomeIcon icon={faWifi} className="generalStyles__userLoggedIcon" />
                  {"  "}Edge Server
                </Tag>
              </div>
            )}
            {socketConnection === "OFFLINE" && (
              <div>
                <Tag
                  color="cyan"
                  className="generalStyles__width150px generalStyles__textCenter generalStyles__textEllipsis"
                >
                  <FontAwesomeIcon icon={faWifi} className=" generalStyles__userLoggedIcon" />
                  {"  "}Offline Server
                </Tag>
              </div>
            )}
            {socketConnection === "EMULATOR" && (
              <div>
                <Tag
                  color="orange"
                  className="generalStyles__width150px generalStyles__textCenter generalStyles__textEllipsis"
                >
                  <FontAwesomeIcon icon={faWifi} className=" generalStyles__userLoggedIcon" />
                  {"  "}Emulator Server
                </Tag>
              </div>
            )}
          </div>
          {/** ************************************************* */}
          {/** USER AVATAR */}
          <div
            className="generalStyles__mlFix25"
            role="button"
            tabIndex={0}
            onClick={() => {
              showDrawer();
            }}
            onKeyDown={() => {
              showDrawer();
            }}
          >
            <Avatar className="button__button1">
              {userLogged && userLogged.firstName ? userLogged.firstName.substring(0, 1) : ""}
              {userLogged && userLogged.lastName ? userLogged.lastName.substring(0, 1) : ""}
            </Avatar>
          </div>
          {/** ************************************************* */}
          {/** CURRENT LANGUAGE ICON */}
          <div>
            <img
              src={language === "en" ? usaFlag : spainFlag}
              alt=""
              width="25"
              height="15"
              className="generalStyles__mlFix"
            />
          </div>
        </div>
      </Spin>
      <Drawer
        width={550}
        placement="right"
        onClose={onClickClose}
        closable={false}
        open={openDrawerUser}
        footer={
          <div className="container generalStyles__flex">
            <div className="generalStyles__width100 generalStyles__mrFix">
              <ButtonClose onClick={onClickClose} />
            </div>
          </div>
        }
      >
        <div className="generalStyles__spaceBetween">
          <div className="generalStyles__flex">
            {/** ************************************************* */}
            {/** USER AVATAR */}
            <div>
              <Avatar size={{ xs: 24, sm: 32, md: 40, lg: 64, xl: 80, xxl: 100 }}>
                {userLogged && userLogged.firstName ? userLogged.firstName.substring(0, 1) : ""}
                {userLogged && userLogged.lastName ? userLogged.lastName.substring(0, 1) : ""}
              </Avatar>
            </div>
            {/** ************************************************* */}
            {/** USER EMAIL AND ROLE DATA */}
            <div className="generalStyles__mlFix25 mt-3">
              <p className="drawer__userNameDrawer">
                {userLogged && userLogged.firstName ? userLogged.firstName : ""}{" "}
                {userLogged && userLogged.middleName ? userLogged.middleName : ""}{" "}
                {userLogged && userLogged.lastName ? userLogged.lastName : ""}
              </p>
              <p className="generalStyles__userLoggedEmail">
                {userLogged && userLogged.email ? userLogged.email : "--"}
              </p>
              <p className="generalStyles__userLoggedEmail">
                <FontAwesomeIcon
                  icon={faCircleCheck}
                  className="generalStyles__success generalStyles__userLoggedIcon"
                />
                <span className="generalStyles__mlFix">
                  {" "}
                  {userLogged && userLogged.role && userLogged.role.name
                    ? userLogged.role.name
                    : "--"}
                </span>
              </p>
            </div>
          </div>
          {/** ************************************************* */}
          {/** SIGN OUT BUTTON */}
          <div>
            <Button
              className="drawer__logoutButton"
              shape="round"
              icon={<FontAwesomeIcon icon={faRightFromBracket} className="generalStyles__mrFix" />}
              onClick={signOut}
            >
              {t("header.signOut")}
            </Button>
          </div>
        </div>
        {/** ************************************************* */}
        {userLogged && (
          <div>
            {/** ************************************************* */}
            {/** PASSWORD OFFLINE */}
            {/** <OfflinePsw /> */}
            {/** ************************************************* */}
            {/** GLOBAL CONFIGURATION */}
            {userLogged &&
              userLogged.role &&
              userLogged.role.name &&
              userLogged.role.name === "MASTER ADMIN" && <GlobalConfiguration />}
            {/** ************************************************* */}
            {/** NOTIFICATIONS */}
            <AppNotifications />
            {/** ************************************************* */}
            {/** INTERNATIONALIZATION */}
            <Internationalization />
            {/** ************************************************* */}
            {/** APPEARANCE */}
            <Appearance />
            {/** ************************************************* */}
            {/** ACTION FIRING */}
            <ActionFiringPreference />
            {/** ************************************************* */}
            {/** MULTI-FACTOR AUTHENTICATION */}
            <MultiFactorAuth />
            {/** ************************************************* */}
            {/** USER INFORMATION */}
            <DrawerUserInformation data={userLogged || {}} />
            {/** ************************************************* */}
            {/** ROLE INFORMATION */}
            <DrawerRoleInformation data={userLogged.role || {}} />
            {/** ************************************************* */}
            {/** ORGANIZATION INFORMATION */}
            <DrawerOrganizationInformation data={userLogged.org || {}} />
            {/** ************************************************* */}
            {/** LIFTNET INFORMATION */}
            <LiftnetInformation />
          </div>
        )}
        <br />
      </Drawer>
      <GlobalAlert2
        isError={isErrorGetRole}
        isSuccess={false}
        requestType="GET"
        error={errorGetRole}
        name="Role"
      />
    </div>
  );
};

export default YourProfileDrawer;
